// Header Component - Airbnb-Style Sticky Header

import React, { useState, useEffect } from 'react';
import { MapPin, Menu, Users, Plus, BookOpen } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useConfig } from '../../context/ConfigContext';
import { SearchPill } from './SearchPill';
import { UserMenu } from './UserMenu';
import { RealmSwitcher } from './RealmSwitcher';
import type { PlaceMatchView, GeocodedLocation } from '../../types';

interface HeaderProps {
  onNavigate: (view: PlaceMatchView) => void;
  onSearch?: (query: string) => void;
  searchQuery?: string;
  isModerator?: boolean;
  isAdmin?: boolean;
  favoritesCount?: number;
  pendingCount?: number;
  onAuthRequest?: (mode: 'login' | 'register') => void;
  onMobileSearchClick?: () => void;
  // Location props
  selectedLocation?: string;
  onLocationSelect?: (location: GeocodedLocation) => void;
  onNearbyClick?: () => void;
  isLocating?: boolean;
  nearbyAddress?: string;
}

export function Header({
  onNavigate,
  onSearch,
  searchQuery = '',
  isModerator = false,
  isAdmin = false,
  favoritesCount = 0,
  pendingCount = 0,
  onAuthRequest,
  onMobileSearchClick,
  selectedLocation,
  onLocationSelect,
  onNearbyClick,
  isLocating = false,
  nearbyAddress,
}: HeaderProps) {
  const { user } = useAuth();
  const config = useConfig();
  const [scrolled, setScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleUserMenuNavigate = (action: string) => {
    if (action === 'login' || action === 'register') {
      // Handle auth - use direct callback if available, otherwise config
      if (onAuthRequest) {
        onAuthRequest(action as 'login' | 'register');
      } else {
        config.onAuthRequired?.();
      }
    } else {
      onNavigate(action as PlaceMatchView);
    }
  };

  return (
    <header
      className={`fixed top-0 w-full z-40 bg-white transition-all duration-300 ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo + Realm Switcher */}
          <div className="flex items-center gap-4">
            <div
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => onNavigate('search')}
            >
              <img
                src="/logo.png"
                alt="GoodPlace"
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold tracking-tight hidden md:block text-gray-800">
                goodplace
              </span>
            </div>
            {/* Realm Switcher - Desktop */}
            <div className="hidden lg:block">
              <RealmSwitcher />
            </div>
            {/* Realm Switcher - Mobile/Tablet */}
            <div className="lg:hidden">
              <RealmSwitcher compact />
            </div>
          </div>

          {/* Search Pill - Desktop */}
          <div className="hidden md:block">
            <SearchPill
              value={searchQuery}
              onChange={onSearch}
              scrolled={scrolled}
              selectedLocation={selectedLocation}
              onLocationSelect={onLocationSelect}
              onNearbyClick={onNearbyClick}
              isLocating={isLocating}
              nearbyAddress={nearbyAddress}
            />
          </div>

          {/* Mobile Search Trigger */}
          <div className="md:hidden flex-1 mx-4">
            <div
              className="flex items-center bg-gray-100 rounded-full p-3 shadow-sm cursor-pointer active:scale-95 transition"
              onClick={onMobileSearchClick}
            >
              <MapPin className="w-5 h-5 text-gray-500 ml-2" />
              <div className="ml-3 flex flex-col">
                <span className="text-sm font-semibold">
                  {searchQuery || 'Finde deinen Ort'}
                </span>
              </div>
            </div>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-2">
            {/* Add Place Button */}
            {config.features?.showAddPlace && (
              <button
                onClick={() => onNavigate('create')}
                className="hidden md:flex items-center gap-2 text-sm font-semibold hover:bg-gray-100 px-4 py-2 rounded-full transition"
              >
                <Plus className="w-4 h-4" />
                <span>Ort teilen</span>
              </button>
            )}

            {/* Quick Navigation - Desktop Only */}
            <div className="hidden lg:flex items-center gap-1 ml-2">
              <button
                onClick={() => onNavigate('community')}
                className="p-2 hover:bg-gray-100 rounded-full transition"
                title="Community"
              >
                <Users className="w-5 h-5 text-gray-600" />
              </button>
              <button
                onClick={() => onNavigate('magazine')}
                className="p-2 hover:bg-gray-100 rounded-full transition"
                title="Magazin"
              >
                <BookOpen className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* User Menu Trigger */}
            <div className="relative">
              <div
                className="flex items-center gap-2 border border-gray-300 rounded-full p-1 pl-3 hover:shadow-md transition cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowUserMenu(!showUserMenu);
                }}
              >
                <Menu className="w-5 h-5 text-gray-600" />
                <div className="bg-gray-500 text-white rounded-full p-1">
                  {user?.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={user.displayName || 'User'}
                      className="w-6 h-6 rounded-full"
                    />
                  ) : (
                    <Users className="w-5 h-5" />
                  )}
                </div>
              </div>

              {/* User Menu Dropdown */}
              {showUserMenu && (
                <UserMenu
                  onClose={() => setShowUserMenu(false)}
                  onNavigate={handleUserMenuNavigate}
                  isModerator={isModerator}
                  isAdmin={isAdmin}
                  favoritesCount={favoritesCount}
                  pendingCount={pendingCount}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;
